import React from 'react';
import { Users, UserCheck, GraduationCap, DollarSign, CalendarCheck, BookOpen, AlertCircle, TrendingUp } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import StatCard from '../../components/common/StatCard';
import { formatCurrency } from '../../utils/helpers';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

function AdminDashboard() {
  const { students, staff, classes, feePayments, feeStructures, attendance, exams, bookIssues, announcements } = useApp();

  const activeStudents = students.filter(s=>s.status==='active').length;
  const activeStaff = staff.filter(s=>s.status==='active').length;
  const totalFeeCollected = feePayments.reduce((sum,p)=>sum+Number(p.amount),0);
  const totalFeeDue = (() => {
    let due = 0;
    students.filter(s=>s.status==='active').forEach(s=>{
      const fs = feeStructures.find(f=>f.classId===s.classId);
      if (!fs) return;
      const prevBal = s.previousYearBalance || 0;
      const paid = feePayments.filter(p=>p.studentId===s.id).reduce((sum,p)=>sum+Number(p.amount),0);
      due += Math.max(0, fs.totalAmount + prevBal - paid);
    });
    return due;
  })();

  const today = new Date().toISOString().split('T')[0];
  const todayAttendance = attendance.filter(a=>a.date===today&&a.entityType==='student');
  const todayPresent = todayAttendance.filter(a=>a.status==='present').length;
  const overdueBooks = bookIssues.filter(i=>!i.returnDate && new Date(i.dueDate) < new Date()).length;

  const attendanceData = [
    { day: 'Mon', present: 8, absent: 2 },
    { day: 'Tue', present: 9, absent: 1 },
    { day: 'Wed', present: 7, absent: 3 },
    { day: 'Thu', present: 10, absent: 0 },
    { day: 'Fri', present: 8, absent: 2 },
  ];

  const feeData = [
    { name: 'Collected', value: totalFeeCollected, color: '#4f46e5' },
    { name: 'Due', value: totalFeeDue, color: '#f87171' },
  ];

  const classData = classes.map(c=>({
    name: c.name+'-'+c.section,
    students: students.filter(s=>s.classId===c.id&&s.status==='active').length
  }));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Admin Dashboard</h1>
        <p className="text-gray-500 text-sm mt-0.5">Welcome back! Here's your school overview.</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={GraduationCap} label="Total Students" value={activeStudents} subtext={classes.length+' classes'} colorClass="bg-indigo-500" />
        <StatCard icon={UserCheck} label="Total Staff" value={activeStaff} subtext="Active members" colorClass="bg-blue-500" />
        <StatCard icon={DollarSign} label="Fee Collected" value={formatCurrency(totalFeeCollected)} subtext="This academic year" colorClass="bg-green-500" />
        <StatCard icon={AlertCircle} label="Fee Due" value={formatCurrency(totalFeeDue)} subtext="Pending collection" colorClass="bg-red-500" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={CalendarCheck} label="Present Today" value={todayPresent+'/'+activeStudents} subtext="Student attendance" colorClass="bg-teal-500" />
        <StatCard icon={BookOpen} label="Overdue Books" value={overdueBooks} subtext="Library" colorClass="bg-orange-500" />
        <StatCard icon={TrendingUp} label="Upcoming Exams" value={exams.filter(e=>e.status==='scheduled').length} subtext="Scheduled" colorClass="bg-purple-500" />
        <StatCard icon={Users} label="Announcements" value={announcements.filter(a=>a.isActive).length} subtext="Active" colorClass="bg-pink-500" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <h3 className="font-semibold mb-4">Weekly Student Attendance</h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={attendanceData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="day" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip />
              <Bar dataKey="present" name="Present" fill="#4f46e5" radius={[4,4,0,0]} />
              <Bar dataKey="absent" name="Absent" fill="#f87171" radius={[4,4,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <h3 className="font-semibold mb-4">Fee Collection</h3>
          <ResponsiveContainer width="100%" height={140}>
            <PieChart>
              <Pie data={feeData} cx="50%" cy="50%" innerRadius={40} outerRadius={65} dataKey="value">
                {feeData.map((entry,i)=><Cell key={i} fill={entry.color} />)}
              </Pie>
              <Tooltip formatter={(val)=>formatCurrency(val)} />
            </PieChart>
          </ResponsiveContainer>
          <div className="mt-3 space-y-1">
            {feeData.map(d=>(
              <div key={d.name} className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{backgroundColor:d.color}}></div>
                  <span className="text-gray-600">{d.name}</span>
                </div>
                <span className="font-medium">{formatCurrency(d.value)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <h3 className="font-semibold mb-4">Students per Class</h3>
          <ResponsiveContainer width="100%" height={160}>
            <BarChart data={classData} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis type="number" tick={{ fontSize: 11 }} />
              <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} width={80} />
              <Tooltip />
              <Bar dataKey="students" name="Students" fill="#6366f1" radius={[0,4,4,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <h3 className="font-semibold mb-3">Recent Announcements</h3>
          <div className="space-y-2">
            {announcements.filter(a=>a.isActive).slice(0,4).map(ann=>(
              <div key={ann.id} className="flex items-start gap-3 py-2 border-b border-gray-50 last:border-0">
                <div className={'w-2 h-2 rounded-full mt-1.5 flex-shrink-0 '+(ann.priority==='high'?'bg-red-500':ann.priority==='medium'?'bg-yellow-500':'bg-green-500')}></div>
                <div>
                  <p className="text-sm font-medium">{ann.title}</p>
                  <p className="">{new Date(ann.createdAt).toLocaleDateString()}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
export default AdminDashboard;
